/* eslint-disable class-methods-use-this */
import { v4 } from 'uuid';

import { ControllerContext } from './controller-context.class';
import { getRegExArray } from './functions';
import { IControllerSubscription } from './interfaces/controller-subscription.interface';
import { Logger } from '../logger-classes';
import { ControllerRootOptions, EventMessage, EventPayloadAction, EventPayloadCrud, EventPayloadCustom, EventPayloadData, EventPayloadObject, EventPayloadType, Subscribers, Subscription } from './types';


export class ControllerRootBase<
	SerializedDataType = null,
	ContextType extends ControllerContext = ControllerContext
> implements IControllerSubscription {
	protected _id: string;
	protected _isNew: boolean;
	protected _instanceOf: string;
	protected _context: ContextType;

	#eventGroup: string;
	#initialized: boolean;
	#subscribers: Subscribers;

	constructor (instanceOf = 'ControllerRoot', options: ControllerRootOptions<SerializedDataType> = {}, context: ContextType | null = null) {
		this.#initialized = false;
		this.#subscribers = {};

		this.#eventGroup = options?.eventGroup ?? 'default';
		this._instanceOf = instanceOf;
		this._isNew = Object.keys(options?.dataIn ?? {}).length === 0;
		if (this._isNew) this._id = v4();

		if (context === null) {
			this._context = new ControllerContext({}) as ContextType;
		} else {
			this._context = context;
		}
	}

	get context (): ContextType { return this._context; }
	get id (): string { return this._id; }
	get initialized (): boolean { return this.#initialized; }
	get instanceOf (): string { return this._instanceOf; }
	get isNew (): boolean { return this._isNew; }
	get logger (): Logger { return this._context.logger; }

	get hasContext (): boolean { return this._context !== null }

	public subscribe <M = EventMessage> (
		nouns: string | Array<string> | Array<RegExp>,
		actions: string | Array<string> | Array<RegExp>,
		callback: (eventMessage: M) => void
	): string | null {
		const subscriptionId = v4();
		const subscription: Subscription<M> = {
			callback,
			nouns: getRegExArray(nouns),
			actions: getRegExArray(actions)
		};

		if (subscription.nouns.length === 0 || subscription.actions.length === 0) {
			this.logger.warn(`${this.instanceOf} -> subscribe`, 'Invalid subscription specified', { nouns, actions, subscription });
			return null;
		}

		// console.log(`subscription:${this.instanceOf.toLowerCase()}`, this.id, subscriptionId, Object.keys(this.#subscribers).length);
		this.#subscribers[subscriptionId] = subscription;
		return subscriptionId;
	}

	public unsubscribe <M> (subscriptionId: string): void {
		delete this.#subscribers[subscriptionId];
	}

	public publishEvent (message: EventMessage): void {
		// treat nouns and actions as regular expressions to match subscribers to the published event
		const subscribers = Object.keys(this.#subscribers).filter((sId) => {
			const subscriber = this.#subscribers[sId];
			const matchNoun = subscriber.nouns.some((regex) => regex.test(message.noun));
			const matchAction = subscriber.actions.some((regex) => regex.test(message.action));
			// console.log(`subscription(${this.instanceOf}):${message.noun}:${message.action}`, matchNoun, matchAction, subscriber.nouns, subscriber.actions);
			return matchNoun && matchAction;
		});

		// console.log(`publishEvent(root):${message.noun}:${message.action}`, this.instanceOf, this.#subscribers, { subscribers });
		// call each subscriber's callback with the message
		// One throwing subscriber must not abort the fan-out — later subscribers (dirty tracking included) would stop seeing mutations until reload.
		subscribers.forEach((sId) => {
			try {
				(this.#subscribers[sId].callback)(message);
			} catch (error) {
				this.logger.error(`${this.instanceOf} -> publishEvent`, 'Subscriber callback threw', { noun: message.noun, action: message.action, error });
			}
		});
	}

	public publish (noun: string, action: string, payload: EventPayloadCrud | EventPayloadAction | EventPayloadCustom | EventPayloadData | EventPayloadObject, payloadType: EventPayloadType = 'crud'): void {
		const message: EventMessage = {
			key: v4(),
			group: this.#eventGroup,
			noun,
			action,
			timestamp: (new Date()).toISOString(),
			payloadType,
			payload: payload as (typeof payload)
		};

		// console.log(`publish:${noun}:${action}`, message);
		this.publishEvent(message);
	}


	publishRequestedAction<D = unknown> (type: string, id: string, data: Record<string, D> | null = null): void {
		this.publish(`${type.toLowerCase()}_action`, 'requested', {
			ref: id,
			data
		});
	}

	publishShowProperties<D = unknown> (type: string, id: string, data: Record<string, D> | null = null): void {
		this.publish(`${type.toLowerCase()}_properties`, 'show', {
			ref: id,
			data
		});
	}

	protected setInitialized (): void { this.#initialized = true; }

	protected serialize (): SerializedDataType { return {} as SerializedDataType; }
}
